(function (root, factory) {
  var api = factory();
  if (typeof module === 'object' && module.exports) module.exports = api;
  else api.init(root);
})(typeof window !== 'undefined' ? window : globalThis, function () {
  'use strict';

  var KEY = 'rhl-cart-continuity:v1';
  var LOCK_KEY = KEY + ':restore-lock';
  var LOCK_AGE = 30 * 1000;
  var mutationVersion = 0;
  var reconcilePending = null;
  var syncTail = Promise.resolve();
  var mutationTail = Promise.resolve();
  var lastCart = null;

  // Shopify's embedded editor can send an old unpartitioned cart cookie
  // alongside its current partitioned cookie. Shopify then creates a fresh
  // empty cart on each read. Preserve the editor cookie; remove only its
  // conflicting host-only legacy cookie. Never alter shopper cookies.
  function repairEditorCartSession(win) {
    if (!win.Shopify || win.Shopify.designMode !== true || win.location.protocol !== 'https:') return false;
    try {
      var values = win.document.cookie.split(';').map(function (cookie) { return cookie.trim(); })
        .filter(function (cookie) { return cookie.indexOf('cart=') === 0; });
      if (new Set(values).size < 2) return false;
      win.document.cookie = 'cart=; Path=/; Max-Age=0; SameSite=None; Secure';
      return true;
    } catch (_) { return false; }
  }

  function cartSignature(cart) {
    if (!cart || !Array.isArray(cart.items) || !Number.isInteger(cart.item_count)) return null;
    return cart.item_count + ':' + cart.items.map(function (item) {
      return item.key + ':' + item.quantity;
    }).sort().join('|');
  }

  function verifyAddedItems(cart, expected) {
    return !expected || expected.every(function (item) {
      var added = validLine(item);
      return added && cart.items.some(function (line) {
        var saved = validLine(line);
        return saved && lineKey(saved) === lineKey(added) && saved.quantity >= added.quantity;
      });
    });
  }

  // Card mutations share a queue, including their final authoritative read.
  // A second product must never race the creation of the first cart session.
  function runMutation(work) {
    mutationVersion += 1;
    var pending = mutationTail.then(work);
    mutationTail = pending.catch(function () {});
    return pending;
  }

  function syncCart(win, detail) {
    repairEditorCartSession(win);
    var version = ++mutationVersion;
    var pending = syncTail.catch(function () {}).then(async function () {
      var components = Array.from(win.document.querySelectorAll('[ref="hdt-cart"]'));
      var sections = Array.from(new Set(components.map(function (node) { return node.getAttribute('section-id'); }).filter(Boolean)));
      if (!sections.length) return fetchCart(win);
      // Read the drawer and its embedded cart snapshot in ONE server render.
      // Display refreshes never POST to cart/update or change quantities.
      var url = new win.URL(win.location.href);
      url.searchParams.set('sections', sections.join(','));
      url.searchParams.set('_rhl_cart_refresh', String(Date.now()));
      // Shopify product URLs negotiate product JSON for application/json;
      // use the normal page Accept header so sections are rendered on PDPs too.
      var response = await win.fetch(url.href, { credentials: 'same-origin', cache: 'no-store', headers: { Accept: 'text/html' } });
      if (!response.ok) throw new Error('Could not refresh your enquiry list. Please reopen it to try again.');
      var rendered = await response.json();
      var snapshot;
      for (var id of sections) {
        var html = new win.DOMParser().parseFromString(rendered[id] || '', 'text/html');
        var node = html.querySelector('template[data-rhl-cart-state]');
        if (node) { snapshot = parse(node.content.textContent); break; }
      }
      // Ajax cart is the authority. A stale/empty Liquid section may never
      // overwrite a saved cart or produce a false successful-add message.
      var cart = await fetchCart(win);
      var isCartPage = win.location.pathname.replace(/\/$/, '').endsWith('/cart');
      if (snapshot && cartSignature(snapshot) !== cartSignature(cart)) {
        throw new Error('Your enquiry list is still updating. Please reopen it to check your items.');
      }
      if (!verifyAddedItems(cart, detail && detail.rhlExpectedItems)) {
        throw new Error('We could not confirm this item in your enquiry list. Open your enquiry list to check before adding again.');
      }
      if ((!snapshot && !isCartPage) || !cart || !Array.isArray(cart.items) || !Number.isInteger(cart.item_count) ||
          sections.some(function (id) { return typeof rendered[id] !== 'string'; })) {
        throw new Error('Could not refresh your enquiry list. Please reopen it to try again.');
      }
      cart.sections = rendered;
      if (version !== mutationVersion) return cart;
      await applyCart(win, cart, version, detail);
      return cart;
    });
    syncTail = pending;
    return pending;
  }

  function reportSyncError(win, error) {
    // Do not turn an already successful add into an invitation to add it again.
    var drawer = win.document.getElementById('CartDrawer');
    if (drawer) {
      var status = drawer.querySelector('[data-rhl-cart-sync-error]');
      if (!status) {
        status = win.document.createElement('p');
        status.setAttribute('data-rhl-cart-sync-error', '');
        status.setAttribute('role', 'status');
        drawer.querySelector('.hdt-dialog-drawer__header')?.after(status);
      }
      status.textContent = error.message;
    }
  }

  function refreshDisplay(win) {
    return syncCart(win).catch(function (error) { reportSyncError(win, error); });
  }

  // A history/editor navigation can restore stale Liquid markup even while
  // Shopify still holds the correct cart. Refresh the theme's existing cart
  // component after it is ready, without adding the saved items again.
  async function applyCart(win, cart, version, detail) {
    var doc = win.document;
    var components = Array.from(doc.querySelectorAll('[ref="hdt-cart"]'));
    if (win.customElements) {
      await Promise.all(components.map(function (node) {
        return win.customElements.whenDefined(node.localName);
      }));
    }
    if (version !== mutationVersion) return;
    Array.from(doc.querySelectorAll('[data-rhl-card-atc]')).forEach(function (card) {
      var line = cart.items.find(function (item) {
        return String(item.variant_id || item.id) === card.dataset.variantId &&
          !Object.keys(item.properties || {}).length && !item.selling_plan_allocation;
      });
      var quantity = Number(line && line.quantity) || 0;
      card.dataset.quantity = String(quantity);
      if (line) card.dataset.lineKey = line.key;
      else delete card.dataset.lineKey;
      var compact = win.matchMedia('(hover: none), (max-width: 767px)').matches;
      var add = card.querySelector('[data-rhl-card-add]');
      var controls = card.querySelector('[data-rhl-card-quantity]');
      var output = card.querySelector('[data-rhl-card-value]');
      if (add) add.hidden = !compact && quantity > 0;
      if (controls) controls.hidden = compact || quantity < 1;
      if (output) output.value = output.textContent = String(quantity);
    });
    lastCart = cart;
    recordCart(win, cart, true);
    doc.querySelector('[data-rhl-cart-sync-error]')?.remove();
    if (components.length) {
      doc.dispatchEvent(new win.CustomEvent('cart:update', { bubbles: true, detail: Object.assign({
        source: 'rhl-cart-sync', sourceId: 'rhl-cart-sync', actionAfterATC: 'no_action', resource: {}
      }, detail || {}, { cartData: cart, rhlCanonical: true }) }));
    }
  }

  function routeRoot(win) {
    var root = win.Shopify && win.Shopify.routes && win.Shopify.routes.root || '/';
    return root.replace(/\/?$/, '/');
  }

  function parse(raw) {
    try { return JSON.parse(raw); } catch (_) { return null; }
  }

  function validLine(line) {
    var variantId = Number(line && (line.id || line.variant_id));
    var quantity = Number(line && line.quantity);
    if (!Number.isSafeInteger(variantId) || variantId < 1 || !Number.isSafeInteger(quantity) || quantity < 1) return null;
    var result = { id: variantId, quantity: quantity };
    if (line.properties && typeof line.properties === 'object' && !Array.isArray(line.properties)) result.properties = line.properties;
    var plan = line.selling_plan || (line.selling_plan_allocation && line.selling_plan_allocation.selling_plan && line.selling_plan_allocation.selling_plan.id);
    if (Number.isSafeInteger(Number(plan)) && Number(plan) > 0) result.selling_plan = Number(plan);
    return result;
  }

  function linesFrom(cart) {
    if (!cart || !Array.isArray(cart.items)) return null;
    return cart.items.map(validLine).filter(Boolean);
  }

  function readSnapshot(win) {
    var value;
    try { value = parse(win.localStorage.getItem(KEY)); } catch (_) { return null; }
    if (!value || !Number.isFinite(Number(value.savedAt)) || !Array.isArray(value.items)) {
      try { win.localStorage.removeItem(KEY); } catch (_) {}
      return null;
    }
    var items = value.items.map(validLine).filter(Boolean).slice(0, 50);
    return items.length ? { savedAt: Number(value.savedAt), items: items } : null;
  }

  function writeLines(win, items) {
    try {
      if (!items || !items.length) {
        win.localStorage.removeItem(KEY);
        return;
      }
      win.localStorage.setItem(KEY, JSON.stringify({ savedAt: Date.now(), items: items.slice(0, 50) }));
    } catch (_) { /* The Shopify cart itself remains authoritative when storage is unavailable. */ }
  }

  function recordCart(win, cart, clearWhenEmpty) {
    var items = linesFrom(cart);
    if (!items) return;
    if (items.length) writeLines(win, items);
    else if (clearWhenEmpty) writeLines(win, []);
  }

  function propertiesKey(properties) {
    if (!properties || typeof properties !== 'object') return '';
    return JSON.stringify(Object.keys(properties).sort().map(function (key) { return [key, properties[key]]; }));
  }

  function lineKey(line) {
    return [line.id, propertiesKey(line.properties), line.selling_plan || ''].join('|');
  }

  function recordAddedLine(win, detail) {
    var item = detail && detail.cartData;
    if (!item || typeof item !== 'object') return false;
    var requested = Number(detail.data && detail.data.itemCount);
    var added = validLine(Object.assign({}, item, {
      id: item.variant_id || detail.sourceId || item.id,
      quantity: Number.isSafeInteger(requested) && requested > 0 ? requested : item.quantity
    }));
    if (!added) return false;

    var snapshot = readSnapshot(win);
    var items = snapshot ? snapshot.items : [];
    var existing = items.find(function (line) { return lineKey(line) === lineKey(added); });
    if (existing) {
      existing.quantity += added.quantity;
      var responseQuantity = Number(item.quantity);
      if (Number.isSafeInteger(responseQuantity) && responseQuantity > existing.quantity) existing.quantity = responseQuantity;
    } else {
      var responseLine = validLine(Object.assign({}, item, { id: added.id }));
      if (responseLine && responseLine.quantity > added.quantity) added.quantity = responseLine.quantity;
      items.push(added);
    }
    writeLines(win, items);
    return true;
  }

  async function fetchCart(win) {
    repairEditorCartSession(win);
    var response = await win.fetch(routeRoot(win) + 'cart.js', {
      credentials: 'same-origin',
      cache: 'no-store',
      headers: { Accept: 'application/json' }
    });
    if (!response.ok) throw new Error('Cart read failed');
    var cart = await response.json();
    if (!Array.isArray(cart.items)) throw new Error('Cart response was incomplete');
    return cart;
  }

  function reconcile(win) {
    if (reconcilePending) return reconcilePending;
    reconcilePending = reconcileNow(win).finally(function () { reconcilePending = null; });
    return reconcilePending;
  }

  async function reconcileNow(win) {
    var versionAtStart = mutationVersion;
    var cart;
    try { cart = await fetchCart(win); } catch (_) { return; }
    if (versionAtStart !== mutationVersion) return;
    if (cart.items.length) {
      recordCart(win, cart);
      await refreshDisplay(win, cart, versionAtStart);
      return;
    }

    var snapshot = readSnapshot(win);
    if (!snapshot) {
      await refreshDisplay(win, cart, versionAtStart);
      return;
    }
    var lock = 0;
    try { lock = Number(win.localStorage.getItem(LOCK_KEY)) || 0; } catch (_) {}
    if (Date.now() - lock < LOCK_AGE) return;
    var lockValue = Date.now();
    try { win.localStorage.setItem(LOCK_KEY, String(lockValue)); } catch (_) {}

    try {
      var response = await win.fetch(routeRoot(win) + 'cart/add.js', {
        method: 'POST',
        credentials: 'same-origin',
        headers: { Accept: 'application/json', 'Content-Type': 'application/json' },
        body: JSON.stringify({ items: snapshot.items })
      });
      if (!response.ok) return;
      var result = await response.json();
      if (result.status || result.errors) return;
      var restored = await fetchCart(win);
      if (restored.items.length) {
        recordCart(win, restored);
        await refreshDisplay(win, restored, versionAtStart);
        win.document.dispatchEvent(new win.CustomEvent('rhl:cart-restored', { bubbles: true }));
      }
    } catch (_) {
      /* Keep the backup so the next page can retry if the cart endpoint was briefly unavailable. */
    } finally {
      try {
        if (Number(win.localStorage.getItem(LOCK_KEY)) === lockValue) win.localStorage.removeItem(LOCK_KEY);
      } catch (_) {}
    }
  }

  function updateSnapshotForChangeLink(win, url) {
    if (/\/cart\/clear\/?$/.test(url.pathname)) {
      writeLines(win, []);
      return;
    }
    if (!/\/cart\/change\/?$/.test(url.pathname) || url.searchParams.get('quantity') !== '0') return;
    var line = Number(url.searchParams.get('line'));
    var snapshot = readSnapshot(win);
    if (!snapshot || !Number.isSafeInteger(line) || line < 1 || line > snapshot.items.length) return;
    snapshot.items.splice(line - 1, 1);
    writeLines(win, snapshot.items);
  }

  function init(win) {
    if (!win || !win.document || win.__rhlCartContinuityReady) return;
    win.__rhlCartContinuityReady = true;
    var doc = win.document;
    repairEditorCartSession(win);

    win.RHLCartSync = { run: function (work) { repairEditorCartSession(win); return runMutation(work); }, sync: function (detail) { return syncCart(win, detail); } };
    // Capture before native render listeners. Partial add responses and old
    // bundled sections are never allowed to replace the complete cart UI.
    win.addEventListener('cart:update', function (event) {
      var detail = event.detail || {};
      if (detail.rhlCanonical) return;
      event.stopImmediatePropagation();
      var cart = detail.cartData;
      var expected = detail.source === 'product-form-component' && cart ? (cart.items || [cart]) : null;
      syncCart(win, Object.assign({}, detail, { rhlAdded: cart, rhlExpectedItems: expected })).catch(function (error) {
        reportSyncError(win, error);
        doc.dispatchEvent(new win.CustomEvent('cart:error', { bubbles: true }));
      });
    }, true);
    win.addEventListener('cart:reload', function (event) {
      event.stopImmediatePropagation();
      refreshDisplay(win);
    }, true);
    // A native bfcache handler can also render directly. Repair a late stale
    // render by comparing its lines, not only the badge (equal totals can hide
    // different products or variants).
    doc.addEventListener('cart:drawer:render', function () {
      win.setTimeout(function () {
        if (!lastCart) return;
        var rows = Array.from(doc.querySelectorAll('#CartDrawer [data-rhl-cart-line]'));
        var expected = lastCart.items.map(function (item) { return item.key + ':' + item.quantity; }).sort().join('|');
        var actual = rows.map(function (row) { return row.dataset.rhlCartLine + ':' + row.querySelector('[name="updates[]"]')?.value; }).sort().join('|');
        if (actual !== expected) refreshDisplay(win);
      }, 0);
    });
    // Reopening after a failed refresh or another tab's changes always reads
    // the real Shopify cart; it never trusts card labels or cached markup.
    doc.addEventListener('dialog:opening', function (event) {
      if (event.target.id === 'CartDrawer' || event.target.querySelector?.('#CartDrawer')) refreshDisplay(win);
    }, true);

    // Repair before native PDP and drawer requests, as well as quick adds.
    doc.addEventListener('change', function (event) {
      if (event.target.closest?.('[ref="hdt-cart"]')) repairEditorCartSession(win);
    }, true);
    doc.addEventListener('click', function (event) {
      if (event.target.closest?.('[ref="hdt-cart"], form[action*="/cart/add"]')) repairEditorCartSession(win);
      var link = event.target && event.target.closest && event.target.closest('a[href]');
      if (!link) return;
      try { updateSnapshotForChangeLink(win, new win.URL(link.href, win.location.href)); } catch (_) {}
    }, true);

    doc.addEventListener('submit', function (event) {
      var form = event.target;
      if (/\/cart\/add(?:[.?]|$)/.test(form.action || '')) repairEditorCartSession(win);
      if (!form || !form.querySelectorAll || !/\/cart(?:\/update)?\/?$/.test(form.action || '')) return;
      var inputs = Array.from(form.querySelectorAll('[name="updates[]"]'));
      var snapshot = readSnapshot(win);
      if (!snapshot || inputs.length !== snapshot.items.length) return;
      var items = snapshot.items.map(function (item, index) {
        var quantity = Number(inputs[index].value);
        return Number.isSafeInteger(quantity) && quantity > 0 ? Object.assign({}, item, { quantity: quantity }) : null;
      }).filter(Boolean);
      writeLines(win, items);
    }, true);

    // Cover regular/history loads, back-forward cache and editor page changes.
    // A non-persisted pageshow can still contain a cached, empty cart header.
    win.addEventListener('pageshow', function () { reconcile(win); });
    doc.addEventListener('visibilitychange', function () {
      if (doc.visibilityState === 'visible') reconcile(win);
    });
    doc.addEventListener('shopify:section:load', function () { reconcile(win); });
    win.setTimeout(function () { reconcile(win); }, 0);
  }

  return { init: init, reconcile: reconcile, recordCart: recordCart, snapshotKey: KEY, repairEditorCartSession: repairEditorCartSession };
});
